// Employee/Views/DayView.tsx
import React, {useEffect, useMemo, useState} from "react";
import {Box, Table, TableBody, TableCell, TableContainer, TableRow, Typography, useMediaQuery,} from "@mui/material";
import {styles} from "../styles/DayViewStyles";
import {Event, EventAddData} from "../types";
import {addMinutes, differenceInMinutes, format, isWithinInterval, parseISO,} from "date-fns";
import DayEventCard from "../components/View/DayEventCard";
import {useTheme} from "Theme/ThemeContext";

interface DayViewProps {
    events: Event[];
    currentDay: Date;
    handleEventEdit: (event: Event | null) => void;
    handleEventAdd: (eventData: EventAddData) => void;
    onEdit: (event: Event) => void;
    onCreateAvailability: (start: Date, end: Date) => void;
    userType: "client" | "psychologist" | "observer";
    canEdit: boolean;
    timeSlotDuration?: number;
    startHour?: number;
    endHour?: number;
}

const DayView: React.FC<DayViewProps> = ({
                                             events,
                                             currentDay,
                                             handleEventEdit,
                                             handleEventAdd,
                                             canEdit,
                                             userType,
                                             onCreateAvailability,
                                             timeSlotDuration = 60,
                                             startHour = 0,
                                             endHour = 24,
                                         }) => {
    const {theme} = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const componentStyles = styles(theme);

    // State for selection
    const [selectionStart, setSelectionStart] = useState<Date | null>(null);
    const [selectionEnd, setSelectionEnd] = useState<Date | null>(null);
    const [isSelecting, setIsSelecting] = useState(false);
    const [isSelectionValid, setIsSelectionValid] = useState(true);

    // Memoize time slots to avoid unnecessary calculations
    const timeSlots = useMemo(() => {
        return Array.from(
            {length: (endHour - startHour) * (60 / timeSlotDuration)},
            (_, i) => {
                const totalMinutes = startHour * 60 + i * timeSlotDuration;
                const hours = Math.floor(totalMinutes / 60);
                const minutes = totalMinutes % 60;
                return `${hours.toString().padStart(2, "0")}:${minutes
                    .toString()
                    .padStart(2, "0")}`;
            }
        );
    }, [startHour, endHour, timeSlotDuration]);

    // Filter events for the current day
    const filteredEvents = useMemo(() => {
        return events.filter(
            (event) =>
                format(parseISO(event.start), "yyyy-MM-dd") ===
                format(currentDay, "yyyy-MM-dd")
        );
    }, [events, currentDay]);

    // Validate selection based on existing events
    const checkIfSelectionIsValid = (start: Date, end: Date) => {
        return !filteredEvents.some((event) => {
            const eventStart = parseISO(event.start);
            const eventEnd = parseISO(event.end);
            return (
                (start >= eventStart && start < eventEnd) ||
                (end > eventStart && end <= eventEnd) ||
                (start <= eventStart && end >= eventEnd)
            );
        });
    };

    // Handle event selection
    const handleSelectionStart = (timeSlotStart: Date) => {
        if (userType === "psychologist" && canEdit) {
            setSelectionStart(timeSlotStart);
            setSelectionEnd(timeSlotStart);
            setIsSelecting(true);
            setIsSelectionValid(true);
        }
    };

    const handleSelectionEnd = (timeSlotEnd: Date) => {
        if (isSelecting && selectionStart) {
            setIsSelecting(false);
            const isValid = checkIfSelectionIsValid(selectionStart, timeSlotEnd);
            if (isValid) {
                onCreateAvailability(selectionStart, timeSlotEnd);
            }
            setSelectionStart(null);
            setSelectionEnd(null);
        }
    };

    const handleSelectionMove = (timeSlot: Date) => {
        if (isSelecting) {
            const valid = checkIfSelectionIsValid(selectionStart!, timeSlot);
            setSelectionEnd(timeSlot);
            setIsSelectionValid(valid);
        }
    };

    const handleEventAddInDay = (timeSlotStart: Date, timeSlotEnd: Date) => {
        if (userType === "client") {
            const availableEvent = events.find(
                (event) =>
                    event.status === "AVAILABLE" &&
                    isWithinInterval(timeSlotStart, {
                        start: parseISO(event.start),
                        end: parseISO(event.end),
                    })
            );

            if (availableEvent) {
                const eventStart = parseISO(availableEvent.start);
                const eventEnd = parseISO(availableEvent.end);

                const newEventStart =
                    timeSlotStart > eventStart ? timeSlotStart : eventStart;
                const newEventEnd = addMinutes(newEventStart, 60);

                const adjustedNewEventEnd =
                    newEventEnd <= eventEnd ? newEventEnd : eventEnd;

                const eventData: EventAddData = {
                    originalEvent: availableEvent,
                    proposedStart: newEventStart,
                    proposedEnd: adjustedNewEventEnd,
                    timeSlotStart,
                    timeSlotEnd,
                };
                handleEventAdd(eventData);
            }
        }
    };

    const handleEmptyCellClick = (timeSlotStart: Date, timeSlotEnd: Date) => {
        if (userType === "client") {
            handleEventAddInDay(timeSlotStart, timeSlotEnd);
        }
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isSelecting) {
                setIsSelecting(false);
                setSelectionStart(null);
                setSelectionEnd(null);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isSelecting]);

    return (
        <Box
            sx={{
                ...componentStyles.wrapper,
                maxWidth: "100%",
                overflowX: "auto",
                height: isMobile ? "auto" : "100%",
            }}
        >
            <TableContainer
                sx={{
                    ...componentStyles.tableContainer,
                    maxHeight: isMobile ? "none" : "100%",
                }}
            >
                <Table
                    sx={{
                        ...componentStyles.table,
                        width: "100%",
                        minWidth: isMobile ? "300px" : "600px", // Reduced for mobile
                    }}
                >
                    <TableBody
                        sx={{
                            ...componentStyles.tableBody,
                            width: "100%",
                            minWidth: isMobile ? "300px" : "600px", // Reduced for mobile
                        }}
                    >
                        {timeSlots.map((time, index) => {
                            const timeSlotStart = parseISO(
                                `${format(currentDay, "yyyy-MM-dd")}T${time}:00`
                            );
                            const timeSlotEnd = addMinutes(timeSlotStart, 60);

                            return (
                                <TimeSlotRow
                                    key={index}
                                    time={time}
                                    timeSlotStart={timeSlotStart}
                                    timeSlotEnd={timeSlotEnd}
                                    filteredEvents={filteredEvents}
                                    handleEventEdit={handleEventEdit}
                                    handleEventAdd={handleEventAdd}
                                    handleSelectionStart={handleSelectionStart}
                                    handleSelectionEnd={handleSelectionEnd}
                                    handleSelectionMove={handleSelectionMove}
                                    handleEmptyCellClick={handleEmptyCellClick}
                                    isSelecting={isSelecting}
                                    selectionStart={selectionStart}
                                    selectionEnd={selectionEnd}
                                    isSelectionValid={isSelectionValid}
                                    userType={userType}
                                    canEdit={canEdit}
                                    componentStyles={componentStyles}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

// Extracted TimeSlotRow component to simplify the DayView component
const TimeSlotRow = ({
                         time,
                         timeSlotStart,
                         timeSlotEnd,
                         filteredEvents,
                         handleEventEdit,
                         handleSelectionStart,
                         handleSelectionEnd,
                         handleSelectionMove,
                         handleEmptyCellClick,
                         handleEventAdd,
                         isSelecting,
                         selectionStart,
                         selectionEnd,
                         isSelectionValid,
                         userType,
                         canEdit,
                         componentStyles,
                     }: any) => {
    const eventsInTimeSlot = filteredEvents.filter((event: Event) => {
        const eventStart = parseISO(event.start);
        const eventEnd = parseISO(event.end);
        return (
            eventStart.getTime() < timeSlotEnd.getTime() &&
            eventEnd.getTime() > timeSlotStart.getTime() &&
            eventEnd.getTime() !== timeSlotStart.getTime()
        );
    });

    const isSelected =
        selectionStart &&
        selectionEnd &&
        timeSlotStart >= selectionStart &&
        timeSlotStart < selectionEnd;

    const isEndOfEvent = eventsInTimeSlot.some((event: Event) => {
        const eventEnd = parseISO(event.end);
        // Check if the event ends within this time slot (even if not at the exact end of the time slot)
        return eventEnd > timeSlotStart && eventEnd <= timeSlotEnd;
    });

    const isStartOfEvent = eventsInTimeSlot.some((event: Event) => {
        const eventStart = parseISO(event.start);
        return eventStart.getTime() === timeSlotStart.getTime();
    });

    const shouldHideBottomBorder = eventsInTimeSlot.length > 0 && !isEndOfEvent;

    const eventHeightPercentage = (eventStart: Date, eventEnd: Date) => {
        const slotStart = timeSlotStart;
        const slotEnd = timeSlotEnd;
        const effectiveStart = eventStart < slotStart ? slotStart : eventStart;
        const effectiveEnd = eventEnd > slotEnd ? slotEnd : eventEnd;
        const totalMinutesInSlot = 60; // Fixed slot size
        const eventMinutesInSlot = differenceInMinutes(effectiveEnd, effectiveStart);
        return (eventMinutesInSlot / totalMinutesInSlot) * 100;
    };

    const eventTopPercentage = (eventStart: Date) => {
        const minutesFromSlotStart = differenceInMinutes(
            eventStart > timeSlotStart ? eventStart : timeSlotStart,
            timeSlotStart
        );
        return (minutesFromSlotStart / 60) * 100; // 60 minutes per slot
    };

    return (
        <TableRow sx={componentStyles.tableRow}>
            <TableCell sx={componentStyles.timeSlot} component="th" scope="row">
                <Typography>{time}</Typography>
            </TableCell>
            <TableCell
                sx={{
                    ...componentStyles.dayColumn,
                    backgroundColor: isSelected
                        ? isSelectionValid
                            ? "rgba(0, 123, 255, 0.1)"
                            : "rgba(255, 0, 0, 0.1)"
                        : componentStyles.dayColumn.backgroundColor,
                    borderBottom: shouldHideBottomBorder
                        ? "none"
                        : componentStyles.dayColumn.borderBottom,
                    cursor:
                        userType === "psychologist" && canEdit ? "pointer" : "default",
                    position: "relative",
                    height: "100%",
                    padding: 0,
                }}
                onMouseDown={() => handleSelectionStart(timeSlotStart)}
                onMouseUp={() => handleSelectionEnd(timeSlotEnd)}
                onMouseEnter={() => handleSelectionMove(timeSlotStart)}
                onClick={() =>
                    eventsInTimeSlot.length === 0 &&
                    handleEmptyCellClick(timeSlotStart, timeSlotEnd)
                }
            >
                {eventsInTimeSlot.map((event: Event) => {
                    const eventStart = parseISO(event.start);
                    const eventEnd = parseISO(event.end);
                    const isEventStartInSlot = isWithinInterval(eventStart, {
                        start: timeSlotStart,
                        end: timeSlotEnd,
                    });
                    const isEventEndInSlot = isWithinInterval(eventEnd, {
                        start: timeSlotStart,
                        end: timeSlotEnd,
                    });
                    const heightPercentage = eventHeightPercentage(eventStart, eventEnd);
                    const topPercentage = eventTopPercentage(eventStart);

                    return (
                        <Box
                            key={event.id}
                            sx={{
                                cursor: "pointer",
                                border: "none",
                                boxShadow: "none",
                                width: "100%",
                                position: "absolute",
                                left: 0,
                                right: 0,
                                height: `${heightPercentage}%`,
                                top: `${topPercentage}%`,
                                padding: 0,
                                zIndex: 1,
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEventEdit(event);
                            }}
                        >
                            <DayEventCard
                                event={event}
                                onEdit={handleEventEdit}
                                onAdd={handleEventAdd}
                                userType={userType}
                                isStartOfEvent={isEventStartInSlot}
                                isEndOfEvent={isEventEndInSlot}
                                timeSlotStart={timeSlotStart}
                                timeSlotEnd={timeSlotEnd}
                                canEdit={canEdit}
                            />
                        </Box>
                    );
                })}
            </TableCell>
        </TableRow>
    );
};

export default DayView;
